import { Type, type Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  RelationOne,
  RelationField,
  DecimalField,
  StringField,
  TimestampField,
  JsonField,
} from '@ayocore/platform/common/helpers/schema-types';
import accountingDocumentSchemaDefinition from './document.schema';
import accountingCurrencySchemaDefinition from './currency.schema';
import dealRowSchemaDefinition from '@ayocore/erp/crm/schemas/deal-row.schema';
import dealSchemaDefinition from '@ayocore/erp/crm/schemas/deal.schema';
import userSchemaDefinition from '@ayocore/platform/identity/schemas/user.schema';

import { name as moduleName } from '../module.json';
/**
 * Транзакции документов.
 *
 * Описывает связи документа с другими сущностями и движение сумм
 * (например, проведение оплаты).
 */
export const AccountingDocumentTransactionSchema = Type.Object({
  ...SystemFields,

  document: RelationOne(accountingDocumentSchemaDefinition),
  document_id: RelationField(accountingDocumentSchemaDefinition, 'ID документа', {
    description: 'Документ, породивший транзакцию.',
    relationName: 'document',
  }),

  amount: DecimalField({
    title: 'Сумма',
    precision: 14,
    scale: 2,
    description: 'Сумма транзакции в валюте транзакции.',
  }),

  currency: RelationOne(accountingCurrencySchemaDefinition),
  currency_id: RelationField(accountingCurrencySchemaDefinition, 'ID валюты транзакции', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Валюта, в которой выполнена транзакция.',
    relationName: 'currency',
  }),

  deal: RelationOne(dealSchemaDefinition),
  deal_id: RelationField(dealSchemaDefinition, 'ID сделки', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Связь транзакции с сделкой.',
    relationName: 'deal',
  }),

  deal_row: RelationOne(dealRowSchemaDefinition),
  deal_row_id: RelationField(dealRowSchemaDefinition, 'ID строки сделки', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Связь транзакции с конкретной строкой сделки.',
    relationName: 'deal_row',
  }),

  operation_date: TimestampField({
    title: 'Дата операции',
    description: 'Дата фактического исполнения транзакции.',
  }),

  description: StringField({
    title: 'Описание',
    nullable: true,
    maxLength: 500,
    description: 'Короткое описание назначения операции.',
  }),

  metadata: JsonField(Type.Any(), {
    title: 'Дополнительные данные',
    nullable: true,
    description: 'Произвольный JSON с деталями транзакции.',
  }),

  /** Согласование транзакции. */
  approved_at: TimestampField({
    title: 'Дата согласования',
    nullable: true,
    description: 'Дата/время согласования транзакции.',
  }),

  approved_by: RelationOne(userSchemaDefinition),
  approved_by_id: RelationField(userSchemaDefinition, 'Кем согласовано', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Пользователь, согласовавший транзакцию.',
    relationName: 'approved_by',
  }),
});

export type AccountingDocumentTransaction = Static<typeof AccountingDocumentTransactionSchema>;

export default defineSchema({
  moduleName,
  tableName: 'document_transactions',
  schema: AccountingDocumentTransactionSchema,
  additionalIndexes: ['document_id', 'deal_row_id', 'deal_id', 'operation_date'],
  dependencies: [
    accountingDocumentSchemaDefinition.tableName,
    accountingCurrencySchemaDefinition.tableName,
    dealRowSchemaDefinition.tableName,
    dealSchemaDefinition.tableName,
    userSchemaDefinition.tableName,
  ],
});
